import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.svg';
import {
    LayoutDashboard,
    Users,
    FileText,
    CreditCard,
    Settings,
    FileSpreadsheet,
    Menu,
    X,
    LogOut,
    User,
    ChevronLeft,
    ChevronRight,
    Key,
    Plug
} from 'lucide-react';

const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const { user, logout } = useAuth();
    const location = useLocation();

    const navigation = [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Users', href: '/users', icon: Users },
        { name: 'Transcripts', href: '/transcripts', icon: FileText },
        { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard },
        { name: 'Auth Codes', href: '/auth-codes', icon: Key },
        { name: 'Connectors', href: '/connectors', icon: Plug },
        { name: 'Logs', href: '/logs', icon: FileSpreadsheet },
        { name: 'Settings', href: '/settings', icon: Settings },
    ];

    const isCurrentPath = (path) => location.pathname === path;

    return (
        <div className="h-screen bg-gray-50 flex overflow-hidden">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-40 lg:hidden">
                    <div
                        className="fixed inset-0 bg-gray-600 bg-opacity-75"
                        onClick={() => setSidebarOpen(false)}
                    />
                </div>
            )}

            {/* Sidebar */}
            <div className={`
        fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarCollapsed ? 'lg:w-16 sidebar-collapsed' : 'lg:w-64'} w-64
      `}>
                {/* Header */}
                <div className={`flex items-center h-16 px-4 border-b border-gray-200 flex-shrink-0 ${sidebarCollapsed ? 'lg:justify-center' : ''
                    }`}>
                    <div className="flex items-center">
                        <div className="flex-shrink-0">

                        </div>
                        {!sidebarCollapsed && (
                            <div className="ml-3">
                                <img
                                    src={logo}
                                    alt="TokScript Logo"
                                    className="w-40 h-20 object-contain"
                                />
                            </div>
                        )}
                    </div>

                    {/* Mobile close button */}
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden ml-auto text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-6 h-6" />
                    </button>

                    {/* Desktop collapse toggle */}
                    <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        className="hidden lg:block ml-auto text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
                        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {sidebarCollapsed ? (
                            <ChevronRight className="w-5 h-5" />
                        ) : (
                            <ChevronLeft className="w-5 h-5" />
                        )}
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 mt-6 px-3 space-y-1 overflow-y-auto">
                    {navigation.map((item) => {
                        const Icon = item.icon;
                        return (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`
                  group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors relative
                  ${isCurrentPath(item.href)
                                        ? 'bg-primary-100 text-primary-700'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    }
                  ${sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''}
                `}
                                onClick={() => setSidebarOpen(false)}
                                title={sidebarCollapsed ? item.name : ''}
                            >
                                <Icon className={`
                  h-5 w-5 flex-shrink-0 transition-colors
                  ${isCurrentPath(item.href) ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}
                  ${sidebarCollapsed ? 'lg:mr-0' : 'mr-3'}
                `} />
                                {!sidebarCollapsed && (
                                    <span className="truncate">{item.name}</span>
                                )}
                                {/* Active indicator */}
                                {isCurrentPath(item.href) && (
                                    <div className="absolute right-0 top-0 bottom-0 w-1 bg-primary-600 rounded-l-lg" />
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* User info at bottom */}
                <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
                    <div className={`flex items-center ${sidebarCollapsed ? 'lg:justify-center' : ''
                        }`}>
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-gray-600" />
                            </div>
                        </div>
                        {!sidebarCollapsed && (
                            <>
                                <div className="ml-3 flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {user?.name}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                        {user?.email}
                                    </p>
                                </div>
                                <button
                                    onClick={logout}
                                    className="ml-2 text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
                                    title="Logout"
                                >
                                    <LogOut className="w-4 h-4" />
                                </button>
                            </>
                        )}
                        {sidebarCollapsed && (
                            <button
                                onClick={logout}
                                className="lg:hidden text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
                                title="Logout"
                            >
                                <LogOut className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className={`flex-1 flex flex-col overflow-hidden`}>
                {/* Top navigation */}
                <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200">
                    <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="lg:hidden text-gray-500 hover:text-gray-600"
                        >
                            <Menu className="w-6 h-6" />
                        </button>

                        <div className="flex items-center space-x-4">
                            <div className="text-sm text-gray-500">
                                Welcome back, <span className="font-medium text-gray-900">{user?.name}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Page content */}
                <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;